import {
  Atom01,
  CalendarPlus01,
  ClipboardCheck,
  Dataflow01,
  Edit05,
  Eye,
  EyeOff,
  Folder,
  GitMerge,
  LayersThree02,
  PauseCircle,
  Shield01,
  User01,
} from '@kula-ai/icons-core';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { useApplicationHelper as useApplicationHelperDefault } from '@/components/contacts/candidate-job-drawer/useApplicationHelper'; //('todo:cv2');
import { useApplicationHelper as useApplicationHelperV1 } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import useAtsProjectPermissions from '@/hooks/useAtsProjectPermissions';
import { usePermission } from '@/ui/permission';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  TRANSFER_ACTION_STRINGS,
  getTransferDisabledReason,
} from '@/components/ats/actions/transferAction';
import { useIntegrations } from '@/hooks/use-integrations';
import { selectSettings } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { HrisSubMenu } from './components/HrisSubMenu';
import { useHrisIntegrations } from '@/components/ats/hris/useHrisIntegrations';
import { useContext } from 'react';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context';
import {
  Checkr,
  Certn,
  Zinc,
  SpringVerify,
  TransferCandidate,
} from '@/assets/icons/ats';
import {
  Hackerrank,
  Hackerearth,
  CriteriaCorp,
  Hireflix,
  CodeInterview as CodeInterviewIcon,
} from '@/assets/icons/ats';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { SurveyV2 } from '@/assets/icons/interview-plan';
import { useSurveyTooltip } from '@/hooks/useSurveyTooltip';
import { SurveyTooltipContent } from '@/components/shared/survey/components/SurveyTooltipContent';
import { useFraudDetectorActionState } from '@/components/ats/actions/background-check/fraudDetectorActionHelpers';
import type { PermissionGuardReturn } from '@/components/global-search/types';
import { gateActionsOnConsent } from '@/components/consent';
import type { CandidateConsentSummary } from '@/components/consent';

// A declined/withdrawn consent bars every action but rejection (handled outside these menus) and
// anonymize — the erase itself, which has to stay reachable on exactly these candidates. Deleting
// the candidate outright is a removal too, so the contact menu keeps it.
const CONSENT_ALLOWED_ACTIONS = ['anonymize'];
const CONSENT_ALLOWED_CONTACT_ACTIONS = ['anonymize', 'delete'];

type HrisPushPayload = {
  integration: unknown;
  application: unknown;
};

type UseGetApplicationActionsParams = {
  isEmailPresent?: boolean;
  handlePushToHRIS?: (_payload: HrisPushPayload) => void;
  isPending?: boolean;
  isFetchingForm?: boolean;
  activeIntegrationType?: string | boolean | null;
};

const noopHrisPush: NonNullable<
  UseGetApplicationActionsParams['handlePushToHRIS']
> = (_payload: HrisPushPayload) => {};

export const useGetApplicationActions = ({
  isEmailPresent = false,
  handlePushToHRIS = noopHrisPush,
  isPending: isPushingToHRIS = false,
  isFetchingForm = false,
  activeIntegrationType = null,
}: UseGetApplicationActionsParams = {}) => {
  const { isFlowEnabled } = usePermissionGuard() as PermissionGuardReturn;
  const { jobPermissions, flowsPermissions } = useAtsPermissions();
  const settingsDetails = useSelector(selectSettings);
  const { getIntegrationData } = useHrisIntegrations();
  const { getHackerrankStatus } = useSelector(getAtsJobCreate);
  const noHackerrankAccount = getHackerrankStatus?.error ?? false;
  const { resourceUsage } = useContext(CandidateContext);
  const applicationHelper = useApplicationHelperDefault();
  const applicationHelperV2 = useApplicationHelperV1();
  const { applicationData } =
    resourceUsage === 'V2' ? applicationHelperV2 : applicationHelper;

  const {
    ats_job,
    pushed_to_hris = [],
    transfer_info = { history: [], applications: {} },
  } = applicationData || {};
  const {
    feRemoteInterview,
    feCoordinatorAgent,
    feCoordinatorAgentAdhoc,
    feFraudDetection,
    tNLevelTransfer,
    tConsentManagement,
  } = useKFlags();
  // Only the current application's own transfer-out state should block transferring.
  // Being part of a chain (history) must NOT disable the live application, so that
  // multi-hop (A->B->C) and re-transfer (A->B->A) remain possible.
  const transferReason = getTransferDisabledReason({
    application: applicationData,
    transferInfo: transfer_info,
    tNLevelTransfer,
  });
  const disableTransfer = transferReason !== null;
  const { job_status } = ats_job || {};
  const {
    isHackerRankEnabled,
    isHackerEarthEnabled,
    isCriteriaCorpEnabled,
    isCheckrEnabled,
    isCertnEnabled,
    isZincEnabled,
    isSpringVerifyEnabled,
    isHireflixEnabled,
    isCodeInterviewEnabled,
  } = useIntegrations();
  const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
    job_status
  );
  const isApplicationInHiredStage = Boolean(
    applicationData?.ats_job_stages?.some(
      (stage) =>
        stage?.ats_job_milestone?.name === 'Hired' &&
        stage?.id === applicationData?.job_stage_id
    )
  );

  const coordinatorEnabled = applicationData?.ai_coordinator?.enabled === true;
  const activeSessions = applicationData?.ai_coordinator?.active_sessions ?? [];
  // Only disable when API returns a matching ad-hoc session for this application + stage
  const hasAdhocSessionForCurrentStage = activeSessions.some(
    (s) =>
      s?.ats_job_stage_action_id === null &&
      s?.ats_application_id === applicationData?.id &&
      s?.ats_job_stage_id === applicationData?.job_stage_id
  );

  const { hasNoSurveys } = useSurveyTooltip({ enabled: true });

  const surveyTooltipContent = hasNoSurveys ? <SurveyTooltipContent /> : '';

  const { disabled: fraudDetectorDisabled, tooltip: fraudDetectorTooltip } =
    useFraudDetectorActionState(applicationData, feFraudDetection);

  const hrisIntegrations =
    settingsDetails?.account?.account_integrations?.hris_integrations?.filter(
      (integration) => integration?.status === 'active'
    ) || [];

  const showHRISAction =
    isApplicationInHiredStage && hrisIntegrations?.length > 0;

  const handleHrisSubMenuClick = (integration) => {
    handlePushToHRIS({ integration, application: applicationData });
  };

  const availableIntegrations = hrisIntegrations.map((integration) => {
    const { name, type, icon, isImageUrl } = getIntegrationData(
      integration?.type
    );
    const isAlreadyPushed = pushed_to_hris.includes(type);
    const isKomboIntegration = type.includes('kombo');
    // Darwinbox fetches an employee form before push (like Kombo), so the
    // form-fetch spinner applies to it too — first open takes ~1-2s.
    const isDarwinboxIntegration = type === 'darwinbox/darwinbox_api';

    const isIntegrationProcessing =
      isPushingToHRIS === true ||
      activeIntegrationType === type ||
      isPushingToHRIS === type;

    const isLoading = isKomboIntegration
      ? isFetchingForm
      : isIntegrationProcessing || (isDarwinboxIntegration && isFetchingForm);

    return {
      id: type,
      label: name,
      name,
      type,
      icon,
      isImageUrl,
      disabled:
        isAlreadyPushed ||
        ((isKomboIntegration || isDarwinboxIntegration) && isFetchingForm) ||
        (!isKomboIntegration && isIntegrationProcessing),
      added: isAlreadyPushed,
      loading: isLoading,
    };
  });

  return gateActionsOnConsent(
    [
      isJobUpdateAllowedStatus && {
        id: 'scheduleInterview',
        type: 'submenu',
        label: 'Schedule interview (ad-hoc)',
        icon: CalendarPlus01,
        disabled: !isEmailPresent,
        tooltip:
          !isEmailPresent &&
          'This candidate does not have an associated email address',
        children: [
          ...(feCoordinatorAgentAdhoc &&
          feCoordinatorAgent &&
          coordinatorEnabled
            ? [
                {
                  id: 'aiAdhoc',
                  label: 'Schedule with AI (ad-hoc)',
                  icon: Atom01,
                  disabled: hasAdhocSessionForCurrentStage,
                  tooltip: hasAdhocSessionForCurrentStage
                    ? 'An AI coordinator session is already active for this stage.'
                    : undefined,
                },
              ]
            : []),
          {
            id: 'interview',
            label: 'Schedule manually (ad-hoc)',
            icon: CalendarPlus01,
          },
          {
            id: 'candidateSchedule',
            label: 'Send self-schedule link (ad-hoc)',
            icon: User01,
          },
        ],
      },
      {
        id: 'sendSurvey',
        label: 'Send survey (ad-hoc)',
        icon: SurveyV2,
        disabled: hasNoSurveys,
        tooltip: surveyTooltipContent,
        closeDelay: true,
      },
      isJobUpdateAllowedStatus && {
        id: 'sendAssessment',
        label: 'Send assessment (ad-hoc)',
        icon: ClipboardCheck,
        disabled: !isEmailPresent,
        tooltip:
          !isEmailPresent &&
          'This candidate does not have an associated email address',
      },
      isJobUpdateAllowedStatus && {
        id: 'sendAdhocReview',
        label: 'Send review (ad-hoc)',
        icon: Eye,
      },
      jobPermissions?.offer &&
        isJobUpdateAllowedStatus && {
          id: 'initiateOffer',
          label: applicationData?.ats_offer?.id ? 'Edit offer' : 'Create offer',
          icon: Edit05,
        },
      isJobUpdateAllowedStatus && {
        id: 'pause',
        label: 'Pause',
        icon: PauseCircle,
      },
      jobPermissions?.index && {
        id: 'transferToJob',
        label: TRANSFER_ACTION_STRINGS.action,
        icon: TransferCandidate,
        disabled: disableTransfer,
        tooltip:
          transferReason === 'limit'
            ? TRANSFER_ACTION_STRINGS.maxTransfersReached
            : transferReason
              ? TRANSFER_ACTION_STRINGS.alreadyTransferred
              : '',
      },
      jobPermissions?.index && {
        id: 'job',
        label: 'Add to additional job',
        icon: LayersThree02,
      },
      isFlowEnabled && {
        id: 'project',
        label: 'Add to project',
        icon: Folder,
      },
      flowsPermissions?.addCandidate && {
        id: 'flow',
        label: 'Add to Flow',
        icon: Dataflow01,
      },
      feFraudDetection && {
        id: 'fraudDetector',
        label: 'Review with Fraud detector',
        icon: Shield01,
        disabled: fraudDetectorDisabled,
        tooltip: fraudDetectorTooltip,
      },
      tConsentManagement && {
        id: 'anonymize',
        label: 'Anonymize candidate',
        icon: EyeOff,
      },
      showHRISAction && {
        id: 'hris',
        label: 'Add to HRIS',
        type: 'custom',
        component: HrisSubMenu,
        props: {
          integrations: availableIntegrations,
          onIntegrationClick: handleHrisSubMenuClick,
        },
      },
      isJobUpdateAllowedStatus &&
        isHackerRankEnabled && {
          id: 'sendHackerrank',
          label: 'Send HackerRank assessment',
          icon: Hackerrank,
          disabled: !isEmailPresent || noHackerrankAccount,
          tooltip: !isEmailPresent
            ? 'This candidate does not have an associated email address'
            : noHackerrankAccount
              ? 'You need a HackerRank for work account to perform this action'
              : '',
        },
      isJobUpdateAllowedStatus &&
        isHackerRankEnabled && {
          id: 'sendHackerrankInterview',
          label: 'Send HackerRank interview',
          icon: Hackerrank,
          disabled: !isEmailPresent || noHackerrankAccount,
          tooltip: !isEmailPresent
            ? 'This candidate does not have an associated email address'
            : noHackerrankAccount
              ? 'You need a HackerRank for work account to perform this action'
              : '',
        },
      isJobUpdateAllowedStatus &&
        isHackerEarthEnabled && {
          id: 'sendHackerearth',
          label: 'Send HackerEarth assessment',
          icon: Hackerearth,
          disabled: !isEmailPresent,
          tooltip:
            !isEmailPresent &&
            'This candidate does not have an associated email address',
        },
      isJobUpdateAllowedStatus &&
        isCriteriaCorpEnabled && {
          id: 'sendCriteriaCorp',
          label: 'Send CriteriaCorp assessment',
          icon: CriteriaCorp,
          iconProps: {
            width: '17px',
            height: '17px',
          },
          disabled: !isEmailPresent,
          tooltip:
            !isEmailPresent &&
            'This candidate does not have an associated email address',
        },

      isCheckrEnabled && {
        id: 'checkr',
        label: 'Send Checkr assessment',
        icon: Checkr,
        iconProps: {
          width: '18px',
          height: '18px',
        },
      },
      isCertnEnabled && {
        id: 'certn',
        label: 'Send Certn assessment',
        icon: Certn,
        iconProps: {
          width: '18px',
          height: '18px',
        },
        disabled: !isEmailPresent,
        tooltip:
          !isEmailPresent &&
          'This candidate does not have an associated email address',
      },
      isZincEnabled && {
        id: 'zinc',
        label: 'Send Zinc assessment',
        icon: Zinc,
        iconProps: {
          width: '18px',
          height: '18px',
        },
        disabled: !isEmailPresent,
        tooltip:
          !isEmailPresent &&
          'This candidate does not have an associated email address',
      },
      isSpringVerifyEnabled && {
        id: 'springverify',
        label: 'Send SpringVerify assessment',
        icon: SpringVerify,
        iconProps: {
          width: '18px',
          height: '18px',
        },
        disabled: !isEmailPresent,
        tooltip:
          !isEmailPresent &&
          'This candidate does not have an associated email address',
      },
      isJobUpdateAllowedStatus &&
        isHireflixEnabled && {
          id: 'sendHireflix',
          label: 'Send Hireflix assessment',
          icon: Hireflix,
          iconProps: {
            width: '18px',
            height: '18px',
          },
          disabled: !isEmailPresent,
          tooltip:
            !isEmailPresent &&
            'This candidate does not have an associated email address',
        },
      feRemoteInterview &&
        isJobUpdateAllowedStatus &&
        isCodeInterviewEnabled && {
          id: 'sendCodeInterview',
          label: 'Send CodeInterview assessment',
          icon: CodeInterviewIcon,
          iconProps: {
            width: '18px',
            height: '18px',
          },
          disabled: !isEmailPresent,
          tooltip:
            !isEmailPresent &&
            'This candidate does not have an associated email address',
        },
    ].filter(Boolean),
    applicationData?.candidate_consent,
    CONSENT_ALLOWED_ACTIONS
  );
};

export const useGetCandidatesActions = ({
  hasJobs = false,
  consent = null,
}: {
  hasJobs?: boolean;
  consent?: Pick<CandidateConsentSummary, 'status'> | null;
} = {}) => {
  const { isFlowEnabled } = usePermissionGuard() as PermissionGuardReturn;
  const { ats_enabled, tConsentManagement } = useKFlags();
  const { showAddToProjectInContacts } = useAtsProjectPermissions();
  const canEnableDeleteCandidate = usePermission({
    permission: 'api_ats_v1_contact.associated_records?',
  });

  // todo add permission and feature check here
  const items = [
    {
      id: 'flow',
      label: 'Add to Flow',
      icon: Dataflow01,
      checkForEnabling: isFlowEnabled,
    },
    {
      id: 'project',
      label: 'Add to project',
      icon: Folder,
      checkForEnabling: isFlowEnabled,
    },
    {
      id: 'job',
      label: hasJobs ? 'Add to additional job' : 'Add to job',
      icon: LayersThree02,
      checkForEnabling: true,
    },
    { id: 'merge', label: 'Merge', icon: GitMerge, checkForEnabling: true },
    tConsentManagement && {
      id: 'anonymize',
      label: 'Anonymize candidate',
      icon: EyeOff,
      checkForEnabling: true,
    },
    canEnableDeleteCandidate && {
      id: 'delete',
      label: 'Delete candidate',
      color: 'error-200',
      checkForEnabling: true,
    },
    // `cond && {...}` leaves `undefined` in the array while a flag or permission
    // is still resolving, so drop the holes before reading `.id` (as line 455 does).
  ]
    .filter(Boolean)
    .filter((item) => {
      if (item.id === 'project') {
        return !(ats_enabled && !showAddToProjectInContacts());
      }
      return item.checkForEnabling;
    });

  return gateActionsOnConsent(items, consent, CONSENT_ALLOWED_CONTACT_ACTIONS);
};
